<script>
import {useAuthStore} from "../stores/auth";
import {decodeToken} from "../utils/jwt";

export default {
    name: "DashboardLayout",
    props: {
        title: {type: String, required: true},
        subtitle: {type: String, default: ""},
    },
    data() {
        return {
            auth: useAuthStore(),
            companyLogoBroken: false,
            userAvatarBroken: false,
        };
    },
    computed: {
        decodedUser() {
            if (!this.auth.accessToken) return null;
            return decodeToken(this.auth.accessToken);
        },
        currentUser() {
            return this.auth.user || this.decodedUser;
        },
        userInitials() {
            const email = this.currentUser?.email || "";
            if (!email) return "?";
            const parts = email.split("@")[0].split(/[._-]/);
            if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
            return email.slice(0, 2).toUpperCase();
        },
        fullName() {
            return this.currentUser?.fullName || this.currentUser?.userName || this.currentUser?.name || this.currentUser?.unique_name || "User";
        },
        userEmail() {
            return this.currentUser?.email || "";
        },
        userRole() {
            return this.currentUser?.role || "Member";
        },
        companyName() {
            return this.currentUser?.companyName || "No company";
        },
        companyLogoUrl() {
            if (this.companyLogoBroken) return null;
            return this.currentUser?.companyLogoUrl || null;
        },
        companyInitials() {
            const name = this.companyName;
            if (!name || name === "No company") return "NC";
            const words = name.trim().split(/\s+/);
            if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
            return name.slice(0, 2).toUpperCase();
        },
        avatarUrl() {
            if (!this.currentUser) return "";

            if (!this.userAvatarBroken && this.currentUser.avatarUrl) {
                return this.currentUser.avatarUrl;
            }

            const seed = this.currentUser.fullName || this.currentUser.userName || this.currentUser.email || this.currentUser.id || "user";
            return `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(seed)}`;
        },
    },
    methods: {
        handleCompanyLogoError() {
            this.companyLogoBroken = true;
        },
        handleAvatarError() {
            this.userAvatarBroken = true;
        },
        async handleLogout() {
            await this.auth.logout();
            this.$router.push("/login");
        },
    },
};
</script>

<template>
    <div class="layout">
        <aside class="layout__sidebar">
            <div class="layout__brand-row">
                <div class="layout__brand">
                    <img src="/assets/images/logo.png" alt="Logo" class="layout__brand-logo" />
                </div>
            </div>

            <div class="layout__nav-body">
                <div class="layout__nav-section">
                    <nav class="layout__nav">
                        <RouterLink class="layout__nav-link" to="/dashboard">
                            <PhHouse class="layout__nav-icon" :size="20" weight="regular" />
                            Dashboard
                        </RouterLink>

                        <RouterLink class="layout__nav-link" to="/leads">
                            <PhAddressBook class="layout__nav-icon" :size="20" weight="regular" />
                            Leads
                        </RouterLink>
                        <RouterLink class="layout__nav-link" to="/chat-history">
                            <PhArticle class="layout__nav-icon" :size="20" weight="regular" />
                            Chat History
                        </RouterLink>
                        <RouterLink class="layout__nav-link" to="/visitors">
                            <PhUsers class="layout__nav-icon" :size="20" weight="regular" />
                            Visitors
                        </RouterLink>
                    </nav>
                </div>

                <div class="layout__nav-section layout__nav-section--explore">
                    <nav class="layout__nav">
                        <RouterLink class="layout__nav-link" to="/ai-agents">
                            <PhRobot class="layout__nav-icon" :size="20" weight="regular" />
                            AI Agents
                        </RouterLink>
                        <RouterLink class="layout__nav-link" to="/integrations">
                            <PhPlugs class="layout__nav-icon" :size="18" weight="regular" />
                            Integrations
                        </RouterLink>
                        <RouterLink class="layout__nav-link" to="/settings">
                            <PhGearSix class="layout__nav-icon" :size="18" weight="regular" />
                            Settings
                        </RouterLink>
                    </nav>
                </div>
            </div>

            <div class="layout__sidebar-bottom">
                <div v-if="currentUser" class="layout__profile-card">
                    <img class="layout__profile-avatar" :src="avatarUrl" alt="User avatar" @error="handleAvatarError" />
                    <div class="layout__profile-meta">
                        <span class="layout__profile-name">{{ fullName }}</span>
                        <span class="layout__profile-email">{{ userEmail }}</span>
                        <span class="layout__profile-role">{{ userRole }}</span>
                    </div>
                </div>

                <button class="layout__logout-btn" @click="handleLogout">
                    <PhSignOut :size="18" weight="regular" />
                    Log out
                </button>
            </div>
        </aside>

        <main class="layout__main">
            <header class="layout__topbar">
                <div class="layout__topbar-left">
                    <h1 class="layout__topbar-title">{{ title }}</h1>
                    <span v-if="subtitle" class="layout__topbar-subtitle">{{ subtitle }}</span>
                </div>
                <div class="layout__topbar-right">
                    <div v-if="currentUser" class="layout__company">
                        <img v-if="companyLogoUrl" class="layout__company-logo" :src="companyLogoUrl" alt="Company logo" @error="handleCompanyLogoError" />
                        <div v-else class="layout__company-fallback">{{ companyInitials }}</div>
                        <div class="layout__company-meta">
                            <span class="layout__company-name">{{ companyName }}</span>
                        </div>
                    </div>
                </div>
            </header>

            <!-- Page content -->
            <section class="layout__content">
                <slot />
            </section>
        </main>
    </div>
</template>
